import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { IntegrationServices, useQuery } from '@sequencehq/api/utils'
import { Banner, Button, Flex } from '@sequencehq/core-components'
import { GreyGrey60 } from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'
import Spinner from 'components/Loading/Spinner'
import {
  ResolveCustomerLinkButton,
  useReconnectToIntegration
} from 'Integrations/index'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { willAutoLinkCustomers } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'
import { AggregatedCreditNote, CreditNoteStatus } from 'CreditNotes/types'
import { match } from 'ts-pattern'

const useCreditNoteIntegrationWarningBar = (
  creditNote: AggregatedCreditNote
) => {
  const viableIntegrationsQuery = useQuery(
    dashboardv20240509Client.getIntegrations,
    undefined,
    {
      staleTime: 1 * MINUTES,
      select: response => {
        if (!response) {
          return []
        }

        return (
          response.items
            .filter(
              int =>
                int.syncedEntityTypes.includes('CREDIT_NOTE') &&
                int.state === 'CONNECTED'
            )
            .map(int => ({
              syncedEntityTypes: int.syncedEntityTypes,
              service: int.service,
              authFailure: int.authFailure
            })) ?? []
        )
      }
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: creditNote.customerId
    },
    {
      select: (
        customer
      ): {
        name: string
        externalLinks: Partial<{ [service in IntegrationServices]: string }>
      } | null => {
        if (!customer) {
          return null
        }
        return {
          name: customer.legalName,
          externalLinks: customer.integrationIds.reduce(
            (acc, customerLink) => ({
              ...acc,
              [customerLink.service]: customerLink.id
            }),
            {}
          )
        }
      }
    }
  )

  const autoLinkCustomerServicesQuery = useQuery(
    dashboardv99990101Client.getEntityTypeSyncToggles,
    undefined,
    {
      staleTime: 1 * MINUTES,
      select: response =>
        new Set(
          (response?.items ?? [])
            .filter(willAutoLinkCustomers)
            .map(toggle => toggle.integrationService)
        )
    }
  )

  const isAwaitingIssue = match<CreditNoteStatus, boolean>(creditNote.status)
    .with('DRAFT', 'IN_PROGRESS', () => true)
    .with('FINAL', 'SENT', 'VOIDED', () => false)
    .exhaustive()

  const authFailures = viableIntegrationsQuery.data
    ?.filter(int => int.authFailure)
    .map(int => ({
      service: int.service,
      serviceName: getIntegrationName(int.service)
    }))

  const missingCustomerLinks = viableIntegrationsQuery.data
    ?.filter(
      int =>
        int.syncedEntityTypes.includes('CUSTOMER') &&
        !customerQuery.data?.externalLinks[int.service]
    )
    /**
     * Don't bother with a warning if we're already showing one for an auth failure
     */
    .filter(int => !authFailures?.find(af => af.service === int.service))
    .map(int => ({
      service: int.service,
      serviceName: getIntegrationName(int.service),
      willAutoLinkOnIssue:
        isAwaitingIssue &&
        Boolean(autoLinkCustomerServicesQuery.data?.has(int.service))
    }))

  const { reconnect, reconnecting } = useReconnectToIntegration()

  return {
    isLoading: viableIntegrationsQuery.isPending || customerQuery.isPending,
    customerName: customerQuery.data?.name,
    customerId: creditNote.customerId,
    missingCustomerLinks,
    authFailures,
    reconnect,
    reconnecting,
    isAwaitingIssue
  }
}

const buildWillSyncMessage = ({
  customerName,
  serviceName
}: {
  customerName: string | undefined
  serviceName: string
}): string =>
  `${customerName ?? 'The customer'} isn't linked to ${serviceName}. We'll try to link them automatically when this credit note is issued.`

const buildWontSyncMessage = ({
  customerName,
  serviceName,
  isAwaitingIssue
}: {
  customerName: string | undefined
  serviceName: string
  isAwaitingIssue: boolean
}): string => {
  const syncability = isAwaitingIssue ? "won't" : "can't"
  return `Credit note ${syncability} be synced to ${serviceName} because ${customerName ?? 'the customer'} isn't linked.`
}

export const CreditNoteIntegrationWarningBanners = (props: {
  creditNote: AggregatedCreditNote
}) => {
  const hook = useCreditNoteIntegrationWarningBar(props.creditNote)

  if (
    hook.isLoading ||
    (hook.missingCustomerLinks?.length === 0 && hook.authFailures?.length === 0)
  ) {
    return null
  }

  return (
    <Flex flexDirection="column" gap="8px" paddingBottom="24px">
      {hook.missingCustomerLinks?.map(int => (
        <Banner
          variant="warning"
          alignIcon="center"
          key={`${int.service}-customer`}
          data-testid={`creditNote.integrationWarnings.${int.service}.customer`}
          action={
            <ResolveCustomerLinkButton
              service={int.service}
              customerId={hook.customerId}
            />
          }
        >
          {int.willAutoLinkOnIssue
            ? buildWillSyncMessage({
                customerName: hook.customerName,
                serviceName: int.serviceName
              })
            : buildWontSyncMessage({
                customerName: hook.customerName,
                serviceName: int.serviceName,
                isAwaitingIssue: hook.isAwaitingIssue
              })}
        </Banner>
      ))}
      {hook.authFailures?.map(int => (
        <Banner
          variant="warning"
          alignIcon="center"
          key={`${int.service}-auth`}
          data-testid={`creditNote.integrationWarnings.${int.service}.auth`}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void hook.reconnect(int.service)
              }}
              disabled={hook.reconnecting}
            >
              <Flex gap="8px" alignItems="center">
                {hook.reconnecting && (
                  <Spinner height="16px" width="16px" color={GreyGrey60} />
                )}{' '}
                {hook.reconnecting ? 'Resolving...' : 'Resolve'}
              </Flex>
            </Button>
          }
        >
          Credit note {hook.isAwaitingIssue ? "won't" : "can't"} be synced to{' '}
          {int.serviceName} because of an authentication failure.
        </Banner>
      ))}
    </Flex>
  )
}
